<template>
    <Dialog ref="dialog" size="lg" @hidden="cancel">
        <template #title>
            {{ title }}
            <small class="d-block fw-normal fs-6 text-body-secondary mt-1">{{ subject }}</small>
        </template>

        <template #body>
            <div v-if="state === 'loading'" class="text-center py-5">
                <i class="fas fa-circle-notch fa-spin fa-lg text-body-secondary"></i>
            </div>

            <div v-else-if="state === 'choosing'">
                <div v-if="options.templates.length === 0" class="text-center py-5">
                    Pro tento odhad teď není k dispozici žádná šablona.
                </div>

                <template v-else>
                    <div v-for="notice in errors" :key="notice.title" class="alert alert-danger py-2 small">
                        <strong class="d-block mb-1">{{ notice.title }}</strong>{{ notice.message }}
                    </div>

                    <label v-for="notice in confirmations" :key="notice.title" class="d-flex border rounded p-2 small mb-2 cursor-pointer">
                        <input v-model="confirmed" type="checkbox" class="form-check-input mt-0 me-2 flex-shrink-0" :value="notice">
                        {{ notice.message }}
                    </label>

                    <div v-for="notice in informations" :key="notice.title" class="small text-body-secondary mb-3">
                        <i class="fas fa-info-circle me-1"></i>{{ notice.message }}
                    </div>

                    <ul v-if="composedTemplates.length > 0 && pdfTemplates.length > 0" class="nav nav-tabs mb-3">
                        <li class="nav-item">
                            <button type="button" class="nav-link" :class="{active: !isPdfTabActive}" @click="openTab(composedTemplates)">Nové šablony</button>
                        </li>
                        <li class="nav-item">
                            <button type="button" class="nav-link" :class="{active: isPdfTabActive}" @click="openTab(pdfTemplates)">
                                PDF šablony
                                <span class="badge rounded-pill text-bg-secondary ms-1">{{ pdfTemplates.length }}</span>
                            </button>
                        </li>
                    </ul>

                    <div v-if="!isPdfTabActive" class="d-grid gap-2">
                        <label
                            v-for="template in composedTemplates"
                            :key="template.id"
                            class="border rounded p-3 d-flex align-items-center mb-0 cursor-pointer"
                            :class="{'border-primary': template.id === templateId}"
                        >
                            <input v-model="templateId" type="radio" name="document-template" class="form-check-input mt-0 me-3 flex-shrink-0" :value="template.id">
                            <span class="flex-grow-1">
                                <span class="fw-semibold d-block">{{ template.title }}</span>
                                <span v-if="template.description !== null" class="small text-body-secondary">{{ template.description }}</span>
                            </span>
                        </label>
                    </div>

                    <template v-else>
                        <div v-for="(group, index) in pdfGroups" :key="group.title" :class="{'mt-3': index > 0}">
                            <div class="small fw-semibold text-body-secondary mb-2">{{ group.title }}</div>
                            <div class="list-group">
                                <label v-for="template in group.templates" :key="template.id" class="list-group-item list-group-item-action d-flex align-items-center cursor-pointer">
                                    <input v-model="templateId" type="radio" name="document-template" class="form-check-input mt-0 me-3 flex-shrink-0" :value="template.id">
                                    <span class="flex-grow-1">{{ template.title }}</span>
                                    <i class="far fa-file-pdf text-body-secondary ms-2"></i>
                                </label>
                            </div>
                        </div>
                    </template>

                    <template v-if="selectedTemplate !== null && selectedTemplate.formats.length > 1">
                        <div class="small fw-semibold text-body-secondary mt-3 mb-2">Formát</div>
                        <div class="row row-cols-1 row-cols-sm-2 g-2">
                            <div v-for="option in formatOptions" :key="option.format" class="col">
                                <label class="border rounded px-3 py-2 d-flex align-items-center mb-0 h-100 cursor-pointer" :class="{'border-primary': option.format === format}">
                                    <input v-model="chosenFormat" type="radio" name="document-format" class="form-check-input mt-0 me-3 flex-shrink-0" :value="option.format">
                                    <span class="flex-grow-1">
                                        <span class="fw-semibold d-block">{{ option.title }}</span>
                                        <span class="small text-body-secondary">{{ option.description }}</span>
                                    </span>
                                    <i class="far fa-lg text-body-secondary ms-2" :class="option.icon"></i>
                                </label>
                            </div>
                        </div>
                    </template>

                    <div v-for="notice in warnings" :key="notice.title" class="alert alert-warning d-flex flex-wrap align-items-center mt-3 mb-0 py-2">
                        <img v-if="notice.imageUrl !== null" :src="notice.imageUrl" alt="" width="72" height="54" class="rounded bg-white object-fit-cover me-3 flex-shrink-0 d-none d-sm-block">
                        <div class="flex-grow-1 small">
                            <strong class="d-block">{{ notice.title }}</strong>{{ notice.message }}
                        </div>
                        <a v-if="photosUrl !== '' && notice.imageUrl !== null" :href="photosUrl" class="btn btn-outline-primary btn-sm ms-sm-3 mt-2 mt-sm-0 flex-shrink-0">Přidat fotografie</a>
                    </div>

                    <p v-if="!options.isLicensed && licenseUrl !== ''" class="small text-body-secondary mt-3 mb-0">
                        <i class="fas fa-info-circle me-1"></i>Podrobný a zjednodušený odhad jsou součástí předplatného.
                        <a :href="licenseUrl">Zobrazit možnosti</a>
                    </p>
                </template>
            </div>

            <div v-else-if="state === 'generating'" class="text-center py-5">
                <div class="h5 mb-1">Připravujeme dokument</div>
                <div class="text-body-secondary mb-4">{{ selectedTemplate?.title }}, {{ format === 'docx' ? 'Word (DOCX)' : 'PDF' }}</div>
                <div class="progress mx-auto w-75 mb-3" style="height: 6px">
                    <div class="progress-bar progress-bar-striped progress-bar-animated w-100"></div>
                </div>
                <div class="small text-body-secondary">
                    {{ isTakingLong ? 'Ještě chvíli, tento dokument trvá déle než obvykle.' : 'Vkládáme mapy, grafy a fotografie. Obvykle to trvá do 10 sekund.' }}
                </div>
            </div>

            <div v-else-if="state === 'done' && document !== null">
                <div v-if="document.missingParts.length === 0" class="text-center py-5">
                    <div class="rounded-circle d-inline-flex align-items-center justify-content-center text-bg-success mb-3" style="width: 56px; height: 56px">
                        <i class="fas fa-check fa-lg"></i>
                    </div>
                    <div class="h5 mb-1">Stažení proběhlo</div>
                    <div class="text-body-secondary mb-3">{{ document.fileName }}, {{ fileSize }}</div>
                    <button type="button" class="btn btn-outline-primary btn-sm" @click="save">
                        <i class="fas fa-download me-2"></i>Stáhnout znovu
                    </button>
                </div>

                <template v-else>
                    <div class="d-flex align-items-center mb-3">
                        <i class="fas fa-check-circle text-success fa-2x me-3"></i>
                        <div>
                            <strong class="d-block">Stažení proběhlo</strong>
                            <span class="small text-body-secondary">{{ document.fileName }}, {{ fileSize }}</span>
                        </div>
                        <button type="button" class="btn btn-outline-primary btn-sm ms-auto" @click="save">
                            <i class="fas fa-download me-2"></i>Stáhnout znovu
                        </button>
                    </div>
                    <div class="alert alert-warning mb-0 py-2 small">
                        <strong class="d-block mb-1">V dokumentu chybí</strong>
                        <ul class="mb-0 ps-3">
                            <li v-for="part in document.missingParts" :key="part">{{ part }}</li>
                        </ul>
                    </div>
                </template>
            </div>

            <div v-else class="text-center py-5">
                <div class="rounded-circle d-inline-flex align-items-center justify-content-center text-bg-danger mb-3" style="width: 56px; height: 56px">
                    <i class="fas fa-exclamation-circle fa-lg"></i>
                </div>
                <div class="h5 mb-1">Dokument se nepodařilo vytvořit</div>
                <div class="text-body-secondary">Chybu už řešíme. Odhad zůstal beze změny, můžete to zkusit znovu.</div>
            </div>
        </template>

        <template #buttons>
            <span v-if="state === 'choosing' && options.templates.length > 0" class="small text-body-secondary me-auto">Stažením se odhad označí jako dokončený.</span>
            <span v-else-if="state === 'done'" class="small text-body-secondary me-auto">Odhad je označený jako dokončený.</span>

            <button v-if="state === 'generating'" type="button" class="btn btn-link text-body-secondary" @click="cancel">Zrušit</button>
            <button v-else type="button" class="btn btn-link text-body-secondary" data-bs-dismiss="modal">Zavřít</button>

            <button v-if="state === 'choosing' && options.templates.length > 0" type="button" class="btn btn-primary px-3" :disabled="!isDownloadable" @click="generate">
                <i class="fas fa-download me-2"></i>Stáhnout dokument
            </button>
            <button v-else-if="state === 'done'" type="button" class="btn btn-primary px-3" @click="state = 'choosing'">
                {{ document !== null && document.missingParts.length > 0 ? 'Vytvořit znovu' : 'Vytvořit jiný dokument' }}
            </button>
            <button v-else-if="state === 'failed'" type="button" class="btn btn-primary px-3" @click="retry">
                <i class="fas fa-redo me-2"></i>Zkusit znovu
            </button>
        </template>
    </Dialog>
</template>

<script lang="ts">
import Dialog from '@/components/common/Dialog.vue';
import {
    canDownload,
    DocumentDownloadOptions,
    DocumentFormat,
    DocumentNotice,
    DocumentOption,
    downloadDocument,
    DownloadedDocument,
    formatSize,
    loadOptions,
    noticesOf,
    preferredChoice,
    rememberChoice,
} from '@/components/estimates/common/documentDialog/documentDownload';
import {saveAs} from 'file-saver';
import {computed, defineComponent, onBeforeUnmount, onMounted, ref} from 'vue';

type State = 'loading' | 'choosing' | 'generating' | 'done' | 'failed';

const OPENING_ELEMENT = '[data-estimate-document-open]';
const TAKING_LONG_AFTER = 15000;
const FORMAT_OPTIONS = [
    {format: 'docx', title: 'Word', description: 'Dokument můžete dál upravovat', icon: 'fa-file-word'},
    {format: 'pdf', title: 'PDF', description: 'Hotový dokument k odeslání', icon: 'fa-file-pdf'},
];

export default defineComponent({
    components: {Dialog},
    props: {
        optionsUrl: {type: String, required: true},
        documentUrl: {type: String, required: true},
        // What the document is about, below the title of the dialog
        subject: {type: String, required: true},
        photosUrl: {type: String, default: ''},
        licenseUrl: {type: String, default: ''},
    },
    setup(props) {
        const dialog = ref<InstanceType<typeof Dialog> | null>(null);
        const state = ref<State>('loading');
        const options = ref<DocumentDownloadOptions>({templates: [], notices: [], isLicensed: true});
        const templateId = ref<string | null>(null);
        const chosenFormat = ref<DocumentFormat>('docx');
        const confirmed = ref<DocumentNotice[]>([]);
        const document_ = ref<DownloadedDocument | null>(null);
        const isTakingLong = ref(false);
        let request: AbortController | null = null;
        let takingLongTimer: number | undefined;

        const selectedTemplate = computed<DocumentOption | null>(() => options.value.templates.find(template => template.id === templateId.value) ?? null);
        const composedTemplates = computed(() => options.value.templates.filter(template => template.formats.includes('docx')));
        const pdfTemplates = computed(() => options.value.templates.filter(template => !template.formats.includes('docx')));
        const isPdfTabActive = computed(() => selectedTemplate.value !== null && !selectedTemplate.value.formats.includes('docx'));
        const pdfGroups = computed(() => [
            {title: 'Vaše šablony', templates: pdfTemplates.value.filter(template => template.isOwn)},
            {title: 'Základní šablony', templates: pdfTemplates.value.filter(template => !template.isOwn)},
        ].filter(group => group.templates.length > 0));
        const notices = computed(() => noticesOf(options.value.notices, templateId.value));
        const ofSeverity = (severity: DocumentNotice['severity']): DocumentNotice[] => notices.value.filter(notice => notice.severity === severity);
        const isDownloadable = computed(() => selectedTemplate.value !== null && canDownload(notices.value, confirmed.value));
        const fileSize = computed(() => document_.value === null ? '' : formatSize(document_.value.blob.size));

        // A template without the chosen format does not change the choice, the next template can have it again
        const format = computed<DocumentFormat>(() => {
            const formats = selectedTemplate.value?.formats ?? [];

            return formats.includes(chosenFormat.value) ? chosenFormat.value : formats[0] ?? chosenFormat.value;
        });

        function newRequest(): AbortSignal {
            request?.abort();
            request = new AbortController();

            return request.signal;
        }

        function stopRequest(): void {
            request?.abort();
            request = null;
            window.clearTimeout(takingLongTimer);
        }

        async function open(): Promise<void> {
            state.value = 'loading';
            document_.value = null;
            confirmed.value = [];
            dialog.value?.show();

            try {
                options.value = await loadOptions(props.optionsUrl, newRequest());
                const choice = preferredChoice(options.value.templates);

                templateId.value = choice.templateId;
                chosenFormat.value = choice.format;
                state.value = 'choosing';
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    state.value = 'failed';
                }
            }
        }

        function save(): void {
            if (document_.value !== null) {
                saveAs(document_.value.blob, document_.value.fileName);
            }
        }

        async function generate(): Promise<void> {
            if (templateId.value === null) {
                return;
            }

            state.value = 'generating';
            isTakingLong.value = false;
            takingLongTimer = window.setTimeout(() => {
                isTakingLong.value = true;
            }, TAKING_LONG_AFTER);

            try {
                document_.value = await downloadDocument(props.documentUrl, templateId.value, format.value, newRequest());
                rememberChoice(options.value.templates, templateId.value, format.value);
                state.value = 'done';
                save();
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    state.value = 'failed';
                }
            } finally {
                window.clearTimeout(takingLongTimer);
            }
        }

        function cancel(): void {
            const wasGenerating = state.value === 'generating';

            stopRequest();

            if (wasGenerating) {
                state.value = 'choosing';
            }
        }

        function retry(): void {
            if (templateId.value === null) {
                void open();
            } else {
                void generate();
            }
        }

        function openTab(templates: DocumentOption[]): void {
            templateId.value = templates[0].id;
        }

        function openOnClick(event: MouseEvent): void {
            if (event.target instanceof Element && event.target.closest(OPENING_ELEMENT) !== null) {
                void open();
            }
        }

        onMounted(() => document.addEventListener('click', openOnClick));
        onBeforeUnmount(() => {
            document.removeEventListener('click', openOnClick);
            stopRequest();
        });

        return {
            dialog,
            state,
            options,
            templateId,
            format,
            chosenFormat,
            formatOptions: FORMAT_OPTIONS,
            confirmed,
            document: document_,
            isTakingLong,
            selectedTemplate,
            composedTemplates,
            pdfTemplates,
            isPdfTabActive,
            pdfGroups,
            openTab,
            isDownloadable,
            fileSize,
            title: computed(() => {
                if (state.value === 'done') {
                    return 'Dokument je hotový';
                }

                return state.value === 'failed' ? 'Dokument se nepodařilo vytvořit' : 'Vytvořit dokument';
            }),
            errors: computed(() => ofSeverity('error')),
            confirmations: computed(() => ofSeverity('confirmation')),
            warnings: computed(() => ofSeverity('warning')),
            informations: computed(() => ofSeverity('information')),
            generate,
            cancel,
            retry,
            save,
        };
    },
});
</script>
